import { useEffect } from "react";
import { useNotifications } from "@/hooks/use-notifications";

interface NotificationSchedulerProps {
  prayerTimes?: {
    fajr: string;
    dhuhr: string;
    asr: string;
    maghrib: string;
    isha: string;
    date: string;
    location: { lat: number; lng: number };
  };
  selectedMosque?: {
    name: string;
    jummah_time?: string;
  };
}

const NotificationScheduler = ({ prayerTimes, selectedMosque }: NotificationSchedulerProps) => {
  const { hasPermission, scheduleJummahReminder } = useNotifications();

  useEffect(() => {
    if (!hasPermission || !prayerTimes) return;

    // Get today's day of the week (0 = Sunday, 5 = Friday)
    const today = new Date();
    const dayOfWeek = today.getDay();
    
    // Only schedule notifications on Friday or for the upcoming Friday
    let jummahDate = new Date();
    
    if (dayOfWeek === 5) {
      // It's Friday today - use today's date
      jummahDate = new Date(today);
    } else if (dayOfWeek < 5) {
      // Friday is later this week
      const daysUntilFriday = 5 - dayOfWeek;
      jummahDate.setDate(today.getDate() + daysUntilFriday);
    } else {
      // Friday was earlier this week, schedule for next Friday
      const daysUntilNextFriday = 7 - dayOfWeek + 5;
      jummahDate.setDate(today.getDate() + daysUntilNextFriday);
    }

    // Use mosque's Jummah time if available, otherwise default to Dhuhr time
    const jummahTimeStr = selectedMosque?.jummah_time || prayerTimes.dhuhr;
    
    // Parse the time string (assuming format like "12:30 PM" or "12:30")
    const [time, period] = jummahTimeStr.split(' ');
    const [hours, minutes] = time.split(':').map(Number);
    
    let jummahHours = hours;
    if (period === 'PM' && hours !== 12) {
      jummahHours += 12;
    } else if (period === 'AM' && hours === 12) {
      jummahHours = 0;
    }
    
    jummahDate.setHours(jummahHours, minutes, 0, 0);

    // Only schedule if the Jummah time is in the future
    if (jummahDate.getTime() > Date.now()) {
      scheduleJummahReminder(jummahDate, selectedMosque?.name);
    }
  }, [hasPermission, prayerTimes, selectedMosque]);

  // This component doesn't render anything, it just handles scheduling
  return null;
};

export default NotificationScheduler;